/**
 * One agent's team channel for a practice or client scope, ready to render:
 * `useAgentChat` plus everything the side panel and the full-page workspace
 * both need and neither should re-implement — live sync, the team roster,
 * mention notifications (unread counts, "viewed" bookkeeping, mark-read),
 * the handoff / invalidation adapters, message-level handlers and the derived
 * turn state. Layout stays with the caller.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { clientIdForRoute } from "@/features/appTabs/tabRoutes";
import { parseCasePath } from "@/features/caseIde/routes";
import { useOfferActions } from "@/features/caseIde/chat/offers/OfferActionsContext";
import { useMessageHighlight } from "@/features/caseIde/chat/useThreadDeepLink";
import type {
  AskUserAnswers,
  InvalidateTarget
} from "@/features/caseIde/types";
import { invalidateClient } from "@/features/clientProfile/lib/clientInvalidation";
import type { ConnectAppsOutput } from "@/features/connectors/types";
import type { ResearchSourceId } from "@/features/research";
import {
  setViewedThread,
  useNotifications
} from "@/features/notifications/NotificationsProvider";

import { lawyerSelectors } from "../../redux/lawyer/selectors";
import { agentPanelStore, type PanelAgent } from "./agentPanelStore";
import type { AgentThread } from "./api/agentApi";
import type { AttachmentFilePart } from "./attachments/types";
import {
  agentLabelAt,
  caseyName,
  specAtFocus,
  type AgentKind,
  type AgentScope,
  type AgentSpec,
  type ApprovalDecision,
  type BuiltinSkillSummary,
  type HandoffResult
} from "./types";
import {
  useAgentChat,
  type AgentMessageMetadata,
  type ChatAdapters,
  type SerializedSelection,
  type TurnFocus
} from "./useAgentChat";
import {
  onScreenKey,
  openOnScreen,
  type OnScreen,
  type RecentOnScreen
} from "./onScreen/onScreen";
import { onScreenStore } from "./onScreen/onScreenStore";
import { useAgentThreadLive } from "./useAgentThreadLive";
import { useMatterStarters } from "./useMatterStarters";
import { useTeamMembers } from "./useTeamMembers";
import {
  latestMessageNs,
  unstampedTailId,
  useThreadRead
} from "./hooks/useThreadRead";

export type UseScopedAgentChatOptions = {
  agent: PanelAgent;
  scope: AgentScope;
  spec: AgentSpec | null;
  /** Names this surface to the notifications feature ("agent-panel", "agent-workspace"). */
  owner: string;
  /** Thread to open on mount (deep link), overriding the remembered one. */
  initialThreadId?: string | null;
  /**
   * Pin the chat to one thread (onboarding's single conversation). See
   * `useAgentChat`.
   */
  fixedThreadId?: string | null;
  /** Message to scroll to and highlight once the transcript is restored. */
  linkedMessageId?: string | null;
  /**
   * An internal agent on the scope instead of its default (the automation builder
   * on the practice scope); see `useAgentChat`.
   */
  agentKind?: AgentKind | null;
  /** The surface's own remembered thread; see `useAgentChat`. */
  threadKey?: string;
  /**
   * Merged into every agent message this surface sends. The research chat
   * passes every library source; other Caseys leave it unset.
   */
  messageMetadata?: AgentMessageMetadata;
  /** Called after a handoff link has been followed (the panel closes itself). */
  onAfterHandoff?: (handoff: HandoffResult) => void;
  /** Called once the server has recorded the open thread as read (refresh a sidebar). */
  onRead?: () => void;
  /**
   * Host hooks a workspace adds on top of the defaults: the Requests IDE sends
   * its selection with each turn, opens artifacts the agent names and refetches
   * its own caches. Its `onInvalidate` runs in addition to the client bus.
   */
  adapters?: Partial<ChatAdapters>;
};

type OnScreenState = {
  current: OnScreen | null;
  recent: RecentOnScreen[];
  dismissedKey: string | null;
};

const NO_STARTERS: string[] = [];
const NO_BUILTIN_SKILLS: BuiltinSkillSummary[] = [];

const practiceGreeting = (firmName: string): string =>
  `I keep an eye on everything that you're working on at ${firmName}: what is due, who is carrying what, and how each matter is moving. Point me at a client or a case and I will work it from here.`;

/** The case or client on screen at `pathname`, if the page is about one. */
export const focusForPath = (pathname: string): TurnFocus | null => {
  const individualId = parseCasePath(pathname)?.ref.individualId;
  if (individualId) return { kind: "case", id: individualId };
  const clientId = clientIdForRoute(pathname);
  return clientId ? { kind: "client", id: clientId } : null;
};

/**
 * The per-turn context the transport sends: the page with its query (the time
 * review card reads it), the focus, the record the page has open and the ones
 * viewed recently. The server checks all of it and uses the focus and the open
 * record as the default targets.
 */
export const turnContextFor = (
  pathname: string,
  search = "",
  onScreen: OnScreenState = onScreenStore.get()
): SerializedSelection => {
  const route = `${pathname}${search}`;
  const open = openOnScreen(onScreen.current, route, onScreen.dismissedKey);
  const openKey = open ? onScreenKey(open) : null;
  const recent = onScreen.recent.filter((r) => onScreenKey(r) !== openKey);
  return {
    artifact: null,
    text: null,
    route,
    focus: focusForPath(pathname),
    open,
    ...(recent.length ? { recent } : {})
  };
};

export const useScopedAgentChat = ({
  agent,
  scope,
  spec,
  owner,
  initialThreadId,
  fixedThreadId,
  linkedMessageId,
  agentKind,
  threadKey,
  messageMetadata,
  onAfterHandoff,
  onRead,
  adapters: extraAdapters
}: UseScopedAgentChatOptions) => {
  const navigate = useNavigate();
  const firstName = useSelector(lawyerSelectors.selectFirstName);
  const { members: teamMembers, me } = useTeamMembers();

  // A handoff's link is the target agent's page (a workspace or the Case IDE)
  // plus `?thread=`; the page reads the thread. Nothing else to do here but go.
  const onHandoff = useCallback(
    (handoff: HandoffResult) => {
      navigate(handoff.link);
      agentPanelStore.close();
      onAfterHandoff?.(handoff);
    },
    [navigate, onAfterHandoff]
  );

  // A client manager's writes (profile, records, documents, intakes, reminders,
  // a new case) report which caches went stale; route them to the client bus
  // so an open profile — or an artifact viewer — refreshes live.
  const extraInvalidate = extraAdapters?.onInvalidate;
  const onInvalidate = useCallback(
    (targets: InvalidateTarget[]) => {
      if (scope.kind === "client") invalidateClient(scope.id, targets);
      extraInvalidate?.(targets);
    },
    [scope.kind, scope.id, extraInvalidate]
  );

  // Per-turn context: the page the user is on, the case or client and the
  // record open there, so "this" resolves without an @mention. The host's own
  // selection (the Requests IDE's) wins on anything it sets.
  const { pathname, search } = useLocation();
  const locationRef = useRef({ pathname, search });
  locationRef.current = { pathname, search };
  const extraSelection = extraAdapters?.selection;
  const selection = useCallback((): SerializedSelection | null => {
    const own = extraSelection?.() ?? null;
    const { pathname: path, search: query } = locationRef.current;
    return { ...turnContextFor(path, query), ...(own ?? {}) };
  }, [extraSelection]);

  const chat = useAgentChat({
    scope,
    me,
    initialThreadId: initialThreadId ?? null,
    fixedThreadId,
    agentKind,
    threadKey,
    messageMetadata,
    adapters: { ...extraAdapters, selection, onHandoff, onInvalidate }
  });
  useAgentThreadLive(owner, chat);
  // Whatever surface shows the thread, looking at it reads it: the Unread
  // view and the sidebar's bolding clear from the panel and workspaces too.
  useThreadRead({
    scope,
    threadId: chat.threadId,
    latestNs: latestMessageNs(chat.messages),
    tailId: unstampedTailId(chat.messages, chat.status === "ready"),
    onRead
  });

  // Mentions: per-thread unread badges; the open thread's are marked read and
  // do not toast while it is on screen.
  const { unreadFor, markAllRead } = useNotifications();
  const unreadByThread = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const n of unreadFor({ scope_kind: scope.kind, scope_id: scope.id })) {
      const tid = n.context?.thread_id;
      if (typeof tid === "string") counts[tid] = (counts[tid] ?? 0) + 1;
    }
    return counts;
  }, [unreadFor, scope.kind, scope.id]);
  const activeUnread = chat.threadId ? unreadByThread[chat.threadId] ?? 0 : 0;
  useEffect(() => {
    setViewedThread(owner, chat.threadId);
    return () => setViewedThread(owner, null);
  }, [owner, chat.threadId]);
  useEffect(() => {
    if (chat.threadId && activeUnread > 0)
      void markAllRead({ thread_id: chat.threadId });
  }, [chat.threadId, activeUnread, markAllRead]);

  const highlightedId = useMessageHighlight(
    linkedMessageId ?? null,
    chat.messages,
    chat.restoring
  );

  const send = useCallback(
    (
      text: string,
      files?: AttachmentFilePart[],
      metadata?: AgentMessageMetadata
    ) =>
      void (files?.length || metadata
        ? chat.send(text, metadata, files)
        : chat.send(text)),
    [chat]
  );

  // The magnifier's library sources go with the next message the user sends,
  // then clear (a queued message waits without them).
  const [researchSources, setResearchSourcesState] = useState<
    ResearchSourceId[]
  >([]);
  const researchRef = useRef(researchSources);
  const setResearchSources = useCallback((next: ResearchSourceId[]) => {
    researchRef.current = next;
    setResearchSourcesState(next);
  }, []);
  /** The picks as message metadata, cleared from the composer once taken. */
  const takeResearchMetadata = useCallback(():
    | AgentMessageMetadata
    | undefined => {
    const picked = researchRef.current;
    if (!picked.length) return undefined;
    setResearchSources([]);
    return { research_sources: picked };
  }, [setResearchSources]);
  const onApproval = useCallback(
    (decision: ApprovalDecision) => void chat.respondToApproval(decision),
    [chat]
  );
  const onAnswer = useCallback(
    (toolCallId: string, answers: AskUserAnswers) =>
      void chat.answerQuestions(toolCallId, answers),
    [chat]
  );
  const onConnectResolved = useCallback(
    (toolCallId: string, output: ConnectAppsOutput) =>
      void chat.resolveConnect(toolCallId, output),
    [chat]
  );
  const offerActions = useOfferActions(chat);

  const streaming = chat.status === "streaming" || chat.status === "submitted";
  const lastAssistantId = useMemo(() => {
    for (let i = chat.messages.length - 1; i >= 0; i -= 1) {
      if (chat.messages[i].role === "assistant") return chat.messages[i].id;
    }
    return null;
  }, [chat.messages]);
  // A lock to show a spinner for: any turn this client is not streaming (a
  // teammate's, our own from another tab, one continued on the worker pool
  // after a server restart). The chat reattaches to it (`resumeStream`) and
  // the live stream then takes over.
  const otherTurn = chat.activeTurn && !streaming ? chat.activeTurn : null;
  const empty = chat.messages.length === 0 && !chat.restoring;
  const label = caseyName(spec?.label ?? agentLabelAt("assistant", scope.kind));
  // Greeting and starters follow the focus: the one assistant greets a client
  // channel differently from a case.
  const atFocus = spec ? specAtFocus(spec, scope.kind) : null;
  const staticStarters = atFocus?.starters ?? NO_STARTERS;
  // What is pressing on the matter leads; the static list fills in (and is
  // all there is until the call returns, or on the practice scope).
  const { starters, reasons: starterReasons } = useMatterStarters(
    scope,
    staticStarters
  );
  const builtinSkills = atFocus?.skills ?? NO_BUILTIN_SKILLS;
  const activeThread: AgentThread | null =
    chat.threads.find((t) => t.id === chat.threadId) ?? null;

  return {
    chat,
    me,
    teamMembers,
    firstName,
    label,
    starters,
    starterReasons,
    /** The built-in procedures the composer offers at this focus. */
    builtinSkills,
    greeting:
      scope.kind === "practice" && scope.displayName
        ? practiceGreeting(scope.displayName)
        : atFocus?.greeting ?? "How can I help you today?",
    supportsPlanMode: spec?.supports_plan_mode ?? false,
    unreadByThread,
    highlightedId,
    send,
    researchSources,
    setResearchSources,
    takeResearchMetadata,
    onApproval,
    onAnswer,
    onConnectResolved,
    offerActions,
    streaming,
    lastAssistantId,
    otherTurn,
    empty,
    activeThread
  };
};

export type ScopedAgentChat = ReturnType<typeof useScopedAgentChat>;
